/**
 * menahub-attribution.js — first-touch attribution for every order
 * v1.1.0 — 2026-10-29
 *
 * Why this exists:
 *   Our Tap-payments checkout (page.custom-checkout) bypasses Shopify's
 *   hosted /checkout, which means Shopify never sees the buyer's
 *   landing URL, referrer, or UTM tags. As a result the Order →
 *   Conversion Summary panel in Shopify Admin stays blank.
 *
 *   We solve that by capturing attribution data on the FIRST page the
 *   visitor lands on, persisting it in localStorage, and:
 *     1) replaying it to the backend when they submit a Tap charge, and
 *     2) writing it into Shopify CART ATTRIBUTES (mh_* keys) so orders
 *        placed through Shopify's OWN hosted checkout (COD / card) carry
 *        the same note_attributes. The backend attribution cron prefers
 *        these mh_* values over Shopify's (degrading) customerJourneySummary.
 *
 * Attribution model: first-touch.
 *   - On the first visit we store landing URL + referrer + UTM tags.
 *   - On later pages we ONLY refresh UTM tags if the new URL has them
 *     (last-non-empty-UTM wins — prevents overwriting paid-ad UTMs
 *     with a later organic page view).
 *   - Cleared once an order successfully completes (the success page
 *     calls window.MenahubAttribution.clear()).
 *
 * Storage key: localStorage["menahub_attr"] — JSON blob, max ~3KB.
 */

(function () {
  'use strict';

  var STORAGE_KEY = 'menahub_attr';
  var CART_SIG_KEY = 'mh_attr_cart_sig'; // sessionStorage — dedupes cart writes
  var MAX_AGE_MS  = 30 * 24 * 60 * 60 * 1000; // 30 days

  function readStore() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      // Expire stale data
      if (parsed.captured_at && (Date.now() - parsed.captured_at) > MAX_AGE_MS) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return parsed;
    } catch (e) { return null; }
  }

  function writeStore(obj) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
    } catch (e) { /* quota / private mode — ignore */ }
  }

  function readCookie(name) {
    try {
      var parts = ('; ' + document.cookie).split('; ' + name + '=');
      if (parts.length === 2) return decodeURIComponent(parts.pop().split(';').shift());
    } catch (e) { /* ignore */ }
    return '';
  }

  // Meta sets _fbp (browser id) and _fbc (click id, derived from fbclid) cookies.
  // Both are required for server-side Conversions API matching. They can appear a
  // moment AFTER first paint, so we also refresh them lazily before a cart sync.
  function refreshFb(store) {
    if (!store) return store;
    var changed = false;
    var fbp = readCookie('_fbp'); if (fbp && store.fbp !== fbp) { store.fbp = fbp; changed = true; }
    var fbc = readCookie('_fbc'); if (fbc && store.fbc !== fbc) { store.fbc = fbc; changed = true; }
    if (changed) writeStore(store);
    return store;
  }

  function parseUtm(href) {
    var out = {};
    try {
      var u = new URL(href);
      // UTM tags (incl. utm_id = the Meta/Google ad id) + ad click ids
      // (gclid = Google Ads, fbclid = Meta/Instagram). Click ids + utm_id
      // identify the paid channel, campaign AND exact ad even without full UTMs.
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
       'utm_id', 'gclid', 'gbraid', 'wbraid', 'fbclid'].forEach(function (k) {
        var v = u.searchParams.get(k);
        if (v) out[k] = v.slice(0, 256);
      });
    } catch (e) { /* bad URL */ }
    return out;
  }

  function capture() {
    var existing = readStore();
    var url      = window.location.href;
    var referrer = document.referrer || '';
    var newUtm   = parseUtm(url);
    var hasNewUtm = Object.keys(newUtm).length > 0;

    if (existing) {
      // Refresh ONLY UTM if new URL has them (last-non-empty-UTM wins)
      if (hasNewUtm) {
        Object.assign(existing, newUtm);
        existing.utm_refreshed_at = Date.now();
        writeStore(existing);
      }
      refreshFb(existing);
      return existing;
    }

    // First visit: snapshot everything
    var snapshot = {
      landing_site:    url.slice(0, 2048),
      referring_site:  referrer.slice(0, 2048),
      user_agent:      (navigator.userAgent || '').slice(0, 512),
      accept_language: (navigator.language || '').slice(0, 64),
      captured_at:     Date.now()
    };
    Object.assign(snapshot, newUtm);
    var fbp = readCookie('_fbp'); if (fbp) snapshot.fbp = fbp;
    var fbc = readCookie('_fbc'); if (fbc) snapshot.fbc = fbc;
    writeStore(snapshot);
    return snapshot;
  }

  // Map the captured attribution to Shopify cart note_attributes (mh_* keys).
  // Returns null when there is nothing marketing-relevant to attach.
  function toCartAttributes(a) {
    var map = {
      mh_utm_source:   a.utm_source,
      mh_utm_medium:   a.utm_medium,
      mh_utm_campaign: a.utm_campaign,
      mh_utm_term:     a.utm_term,
      mh_utm_content:  a.utm_content,
      mh_utm_id:       a.utm_id,
      mh_gclid:        a.gclid || a.gbraid || a.wbraid,
      mh_fbclid:       a.fbclid,
      mh_fbp:          a.fbp,
      mh_fbc:          a.fbc,
      mh_landing:      a.landing_site,
      mh_referrer:     a.referring_site
    };
    var attrs = {}; var has = false;
    Object.keys(map).forEach(function (k) {
      var v = map[k];
      if (v) { attrs[k] = String(v).slice(0, 255); has = true; }
    });
    return has ? attrs : null;
  }

  // Push mh_* attribution onto the Shopify cart so a hosted-checkout order
  // inherits it as note_attributes. Fires at most once per session unless the
  // marketing signal changes, and only when there IS a real signal to attribute.
  function syncCart(force) {
    var a = refreshFb(readStore());
    if (!a) return;
    if (!(a.utm_source || a.utm_medium || a.utm_campaign || a.gclid || a.fbclid || a.utm_id)) return;

    var sig = [a.utm_source, a.utm_medium, a.utm_campaign, a.utm_term,
               a.utm_content, a.utm_id, a.fbclid, a.gclid, a.fbp, a.fbc].join('|');
    try {
      if (!force && sessionStorage.getItem(CART_SIG_KEY) === sig) return;
    } catch (e) { /* sessionStorage blocked — fall through and sync anyway */ }

    var attrs = toCartAttributes(a);
    if (!attrs) return;

    try {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ attributes: attrs }),
        keepalive: true,
        credentials: 'same-origin'
      }).then(function () {
        try { sessionStorage.setItem(CART_SIG_KEY, sig); } catch (e) {}
      }).catch(function () { /* network/offline — retried next page */ });
    } catch (e) { /* fetch unavailable — ignore */ }
  }

  function get() {
    return readStore() || capture();
  }

  function clear() {
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
    try { sessionStorage.removeItem(CART_SIG_KEY); } catch (e) {}
  }

  // Public API
  window.MenahubAttribution = { capture: capture, get: get, clear: clear, syncCart: syncCart };

  // Run immediately (we want the snapshot taken before any link is clicked)
  capture();

  // Then, off the critical path, mirror the signal into the Shopify cart so
  // EVERY checkout path (Tap or Shopify-hosted) carries first-party attribution.
  function runSync() { try { syncCart(false); } catch (e) {} }
  if ('requestIdleCallback' in window) { requestIdleCallback(runSync, { timeout: 2500 }); }
  else { setTimeout(runSync, 1200); }
})();
